import { useMemo } from 'react';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { ISODate, UnresolvedPresetRange } from '@amzn/storm-ui-date-picker-v3';
import dt from '@urc-ui/components/date-input/utils/date-time-fns';
import { logger } from '@urc-ui/services/logger';
import { UseCustomPresetProps, UseDateRangePickerDates, UsePresetsProps } from './types';
import { PRESET_PERIOD } from '@urc-ui/utils/types';
import { useTranslateMap } from '@urc-ui/hooks/translate-map';
import { PRESET_PERIOD_TRANSLATION_MAP, getMaxDateUtc } from '@urc-ui/utils/date-utils';
import { ISO_DATE_FORMAT } from '@urc-ui/components/date-input';

/**
 * List of presets that are available. Any new preset added here should also have a corresponding
 * entry in PRESET_DURATION_MAP
 */
export const PRESETS_MAP: Omit<UnresolvedPresetRange, 'label'>[] = [
  {
    period: PRESET_PERIOD.TODAY,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.YESTERDAY,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-1)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.WEEK_TO_DATE,
    start: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.startOfDay, dt.startOfWeek),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.MONTH_TO_DATE,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.startOfMonth),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.LAST_MONTH,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addMonths(-1), dt.startOfMonth),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addMonths(-1), dt.endOfMonth)
  },
  {
    period: PRESET_PERIOD.LAST_7_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-7)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.LAST_WEEK,
    start: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.startOfDay, dt.addWeeks(-1), dt.startOfWeek),
    end: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.endOfDay, dt.addWeeks(-1), dt.endOfWeek)
  },
  {
    period: PRESET_PERIOD.LAST_30_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-30)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  }
];

export const PRESETS_MAP_HOURLY: Omit<UnresolvedPresetRange, 'label'>[] = [
  {
    period: PRESET_PERIOD.LAST_14_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-14)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  }
];

/**
 *  Map keeping track of the max duration for each preset_period. This is
 *  used when trying to identify the right presets given a reportingWindow.
 *  When adding a new preset, this map should be updated also to include the
 *  duration.
 */
export const PRESET_DURATION_MAP = new Map<PRESET_PERIOD, number>([
  [PRESET_PERIOD.TODAY, 1],
  [PRESET_PERIOD.YESTERDAY, 1],
  [PRESET_PERIOD.LAST_7_DAYS, 7],
  [PRESET_PERIOD.WEEK_TO_DATE, 7],
  [PRESET_PERIOD.LAST_WEEK, 7],
  [PRESET_PERIOD.LAST_14_DAYS, 14],
  [PRESET_PERIOD.MONTH_TO_DATE, 30],
  [PRESET_PERIOD.LAST_30_DAYS, 30],
  [PRESET_PERIOD.THIS_MONTH, 30],
  [PRESET_PERIOD.LAST_MONTH, 30]
]);

interface GetPresetRangeProps {
  // The current date in the marketplace timezone in storm's ISO format
  readonly today: ISODate;

  // minDate in UTC
  readonly minDateUtc: Date;

  // calendar timeZone, which is expected to be the same as marketplace timezone
  readonly timeZone: string;

  readonly locale?: Locale;

  readonly labelMap: Record<string, string>;

  readonly reportingWindow?: number;
}

const isValidPreset = ({ today, minDateUtc, timeZone, locale, reportingWindow }: GetPresetRangeProps, preset: UnresolvedPresetRange) => {
  if (!preset.start) {
    return false;
  }
  const presetStartDateInUtc = preset.start(today, timeZone, locale);

  const isValidStartDate = isAfter(presetStartDateInUtc, minDateUtc) || isSameDay(presetStartDateInUtc, minDateUtc);

  if (!isValidStartDate) {
    logger.logWarningMessage(
      `preset has invalid start date presetStartDateInUtc=${presetStartDateInUtc}, minDateUtc=${minDateUtc}, preset:`,
      preset
    );
  }

  // If there's no reporting window specified, no further checks are needed
  if (reportingWindow === undefined) {
    return isValidStartDate;
  }

  // PRESET_DURATION_MAP has each preset period mapped to its duration. Use this to check if the given preset
  // is valid for this reportingWindow
  const presetDurationInDays = PRESET_DURATION_MAP.has(preset.period as PRESET_PERIOD)
    ? PRESET_DURATION_MAP.get(preset.period as PRESET_PERIOD)
    : undefined;

  if (!presetDurationInDays) {
    logger.logWarningMessage(`preset ${preset.period} is not found in the preset duration map`);
  }

  if (presetDurationInDays !== undefined && presetDurationInDays > reportingWindow) {
    return false;
  }

  return isValidStartDate;
};

const getPresetRanges = (props: GetPresetRangeProps): UnresolvedPresetRange[] => {
  const { labelMap } = props;
  const reportingWindow = props.reportingWindow ? props.reportingWindow : 0;

  let presetsMap = PRESETS_MAP;
  if (reportingWindow) {
    // A reportingWindow is only available for the hourly time unit
    presetsMap = presetsMap.concat(PRESETS_MAP_HOURLY);
  }

  return presetsMap
    .map(preset => ({
      ...preset,
      label: labelMap[preset.period]
    }))
    .filter(preset => isValidPreset(props, preset));
};

const usePresets = ({ todayUtc, minDateUtc, timeZone, locale, defaultPresetPeriod, reportingWindow }: UsePresetsProps) => {
  const minDateISO = minDateUtc ? (formatInTimeZone(minDateUtc, timeZone, 'yyyy-MM-dd') as ISODate) : null;
  const todayISO = formatInTimeZone(todayUtc, timeZone, 'yyyy-MM-dd') as ISODate;
  const labelMap = useTranslateMap(PRESET_PERIOD_TRANSLATION_MAP);

  return useMemo(() => {
    if (minDateUtc === null) {
      return {
        presetRanges: [],
        initialPreset: null
      };
    }

    const presetRanges = getPresetRanges({
      today: todayISO,
      minDateUtc,
      labelMap,
      timeZone,
      locale,
      reportingWindow
    });

    const presetRange = presetRanges.find(range => range.period === defaultPresetPeriod);
    const updatedPreset = presetRange ? presetRange : presetRanges[presetRanges.length - 1];
    const startDate = updatedPreset.start && updatedPreset.start(todayISO, timeZone, locale);
    const endDate = updatedPreset.end && updatedPreset.end(todayISO, timeZone, locale);
    const initialPreset = {
      ...updatedPreset,
      start: startDate,
      end: endDate
    };

    return { presetRanges, initialPreset };
  }, [todayISO, minDateISO, timeZone, locale, defaultPresetPeriod, reportingWindow]);
};

const useDateRangePickerDates = ({ todayUtc, minDateUtc, startDateUtc, timeZone, locale, reportingWindow }: UseDateRangePickerDates) => {
  const minDateISO = formatInTimeZone(minDateUtc, timeZone, 'yyyy-MM-dd') as ISODate;
  const todayISO = formatInTimeZone(todayUtc, timeZone, 'yyyy-MM-dd') as ISODate;

  return useMemo(() => {
    const previousMonth = dt.addMonths(-1)(utcToZonedTime(todayUtc, timeZone));
    const maxDateUtc = getMaxDateUtc(todayUtc, timeZone, startDateUtc, reportingWindow);
    const minDateISO = formatInTimeZone(minDateUtc, timeZone, ISO_DATE_FORMAT);
    const maxDateISO = formatInTimeZone(maxDateUtc, timeZone, ISO_DATE_FORMAT);

    return { previousMonth, minDateISO, maxDateISO };
  }, [todayISO, minDateISO, timeZone, locale, startDateUtc, reportingWindow]);
};

const useCustomPreset = ({ minDateUtc, timeZone, defaultPresetRange, reportingWindow }: UseCustomPresetProps) => {
  return useMemo(() => {
    if (minDateUtc === null || defaultPresetRange === null || PRESET_PERIOD.CUSTOM !== defaultPresetRange.period) {
      return {
        customPreset: null,
        isValidPresetRange: true
      };
    }
    const customStartDateUtc = defaultPresetRange.start as Date;
    const customEndDateUtc = defaultPresetRange.end as Date;
    const formattedStartDate = formatInTimeZone(customStartDateUtc, timeZone, 'yyyy-MM-dd') as ISODate;
    const customStartDate = dt.compose({ today: formattedStartDate, zone: timeZone }, dt.startOfDay);

    // Calculate the maximum allowed end date with a reporting window present
    const maxRwAllowedEndDateUtc =
      reportingWindow && reportingWindow > 0 ? (dt.addDays(reportingWindow - 1)(customStartDateUtc) as Date) : customEndDateUtc;
    const formattedEndDate = formatInTimeZone(customEndDateUtc, timeZone, 'yyyy-MM-dd') as ISODate;
    const customEndDate = dt.compose({ today: formattedEndDate, zone: timeZone }, dt.endOfDay);

    const customPreset = {
      label: PRESET_PERIOD.CUSTOM,
      period: PRESET_PERIOD.CUSTOM,
      start: customStartDate,
      end: customEndDate
    };
    const isValidStartDate = isAfter(customStartDateUtc, minDateUtc) || isSameDay(customStartDateUtc, minDateUtc);
    const isValidEndDate = isBefore(customEndDateUtc, maxRwAllowedEndDateUtc) || isSameDay(customEndDateUtc, maxRwAllowedEndDateUtc);
    const isValidPresetRange = isValidStartDate && isValidEndDate;
    return { customPreset, isValidPresetRange };
  }, [minDateUtc, timeZone, defaultPresetRange, reportingWindow]);
};

export { useDateRangePickerDates, usePresets, useCustomPreset, getPresetRanges };

export type { UseDateRangePickerDates, UsePresetsProps };
